import React, { useEffect, useState } from 'react';
import { Button, Flex, Typography, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SettingOutlined } from '@ant-design/icons';

import Layout from '../../components/layout/Layout';
import ProjectTabs from './ProjectTabs';
import { useStore } from '../../stores/root-store-context';
import { Project } from '../../stores/ProjectsStore';
import { slugify } from '../../routes/RouterUtilities';
import { Helmet } from 'react-helmet';
import { canAccessProject } from '../../helpers/ProjectAccessHelper';

const { Text } = Typography;

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface ProjectContainerLayoutProps {
  children: React.ReactNode;
  breadcrumbTitle?: string;
  onProjectLoaded?: () => void;
}

const ProjectContainerLayout: React.FC<ProjectContainerLayoutProps> = observer(({ children, breadcrumbTitle, onProjectLoaded }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Update params to include projectSlug
  const { projectSlug, projectId, id } = useParams<{ 
    projectSlug?: string, 
    projectId?: string, 
    id?: string 
  }>();
  
  const { applicationStore, projectsStore } = useStore();
  
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const tenant = applicationStore.company?.Tenant || '';
  
  // Determine which identifier to use for loading the project
  const projectIdentifier = projectSlug || projectId || id;
  
  useEffect(() => {
    if (projectIdentifier) {
      setIsLoading(true);
      setError(null);
      
      // Use getBySlug which will handle both slugs and IDs
      projectsStore.getBySlug(projectIdentifier)
        .then(res => {
          if (res) {
            if (!canAccessProject(res, applicationStore.user, "read")) {
              setError(t('Project not found'));
              setProject(null);
              return;
            }

            setProject(res);
            // Update URL if needed (if we loaded by ID but want slug in URL)
            if (!projectSlug && res.title) {
              const slug = slugify(res.title || res.name || '');
              // Only navigate if the current URL doesn't already contain the slug
              if (window.location.pathname.indexOf(slug) === -1) {
                const currentPath = window.location.pathname;
                const newPath = currentPath.replace(
                  /\/projects\/[^/]+/,
                  `/projects/${slug}`
                );
                navigate(newPath, { replace: true });
              }
            }
          } else {
            setError(t('Project not found'));
            // Redirect to projects list after a short delay
            setTimeout(() => {
              navigate(`/${tenant}/projects`);
            }, 3000);
          }
        })
        .catch(error => {
          console.error('Failed to load project:', error);
          setError(t('Failed to load project'));
        })
        .finally(() => {
          setIsLoading(false);
          onProjectLoaded?.();
        });
    }
  }, [projectIdentifier, projectsStore, navigate, tenant, t, applicationStore.user]);
  
  // Format the breadcrumb title to show the project name (for the large text)
  const headerTitle = project 
    ? (project.name || project.title) 
    : (error || (isLoading ? t('Loading project...') : breadcrumbTitle || ''));
  
  // Create breadcrumb items array with proper navigation path
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('Home'), path: '/' },
    { label: t('Projects'), path: `/${tenant}/projects` },
    { label: headerTitle, path: '#' } // Current project (no navigation)
  ];
  
  return (
    <Layout 
      headerStyle={1} 
      footerStyle={2} 
      breadcrumbTitle={headerTitle}
      breadcrumbItems={breadcrumbItems}
    >
      <Helmet>
        <title>{headerTitle}</title>
        <meta name="description" content={headerTitle} />
      </Helmet>
      <div className="container-fluid" style={{ margin: "20px auto" }}>
        <Spin spinning={isLoading}>
          {error ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography.Title level={4} type="danger">{error}</Typography.Title>
              <Typography.Text>Redirecting to projects page...</Typography.Text>
            </div>
          ) : (
            <>
              {/* Navigation Tabs */}
              <ProjectTabs />
              
              {/* Page Content */}
              {children}
            </>
          )}
        </Spin>
      </div>
    </Layout>
  );
});

export default ProjectContainerLayout;
